import { useEffect, useMemo } from 'react';
import { dispatcher } from 'dispatcher';
import { prop, pick } from 'ramda';
import { useDispatch } from 'react-redux';
import { useAppSelector, useUserSelector, useGameConfigurationSelector, useCustomSelector } from 'js/store/hooks';
import { usePrevious } from 'js/hooks/usePrevious';
import * as actions from 'js/components/system/App/store/actions';
import { useGetCMSResourceURL } from 'js/hooks/useGetCMSResourceURL';
import * as actionsJackpots from '@theme/js/hooks/useGetJackpots/store/actions';
import { JACKPOTS_TYPES } from '@theme/js/hooks/useGetJackpots/store/constants';
import { workerService } from 'js/services/worker.service';
import { filterGamesByJackpotCodes, formatJackpotPayload } from './utils';
import { JACKPOTS_REDUCER } from '@core/js/store/constants';

let isFetching = false;

export const useGetOldGamesFeeds = () => {
    const getCMSResourceURL = useGetCMSResourceURL();
    const dispatch = useDispatch();
    const lang = useUserSelector(prop('lang'));
    const {
        requestStamp,
        isOldGamesFeedsLoaded,
        oldGamesFeeds
    } = useAppSelector(pick(['requestStamp', 'isOldGamesFeedsLoaded', 'oldGamesFeeds']));
    const { excludedGameCodes, games: rawGames } = useGameConfigurationSelector(pick(['excludedGameCodes', 'games']));
    const prevExcludedGameCodes = usePrevious(excludedGameCodes);
    const { greenTubeJackpots, skyWindJackpots } = useCustomSelector(JACKPOTS_REDUCER, pick(['greenTubeJackpots', 'skyWindJackpots']));

    const dynamicTags = useAppSelector(prop('dynamicTags'));
    const urlGreenTube = dynamicTags.DTAG_GREENTUBE_JACKPOTS_URL || '';
    const envGreenTube = dynamicTags.DTAG_GREENTUBE_JACKPOTS_ENV || '';
    const urlSkyWind = dynamicTags.DTAG_SKYWIND_JACKPOTS_URL || '';
    const envSkyWind = dynamicTags.DTAG_SKYWIND_JACKPOTS_ENV || '';

    const games = useMemo(() => filterGamesByJackpotCodes(rawGames), [rawGames]);

    useEffect(() => {
        if (!lang || !urlGreenTube || !envGreenTube || !urlSkyWind || !envSkyWind) {
            return;
        }

        if (isOldGamesFeedsLoaded || isFetching) {
            return;
        }

        isFetching = true;
        const requestURL = getCMSResourceURL(`/initialResources/games/${lang}`, { t: requestStamp || Date.now() });

        workerService.request('gameFeeds', { url: requestURL, excludedGameCodes })
            .then((gamesFeeds) => {
                const response = {
                    gamesFeeds,
                    games: rawGames
                };

                /* Fetch Green Tube jackpot data here */
                if (!greenTubeJackpots?.jackpotsGreenTube) {
                    if (urlGreenTube) {
                        fetch(urlGreenTube, { headers: { 'custom-environment': envGreenTube || '' } })
                            .then((res) => res.json())
                            .then((data) => {
                                const jackpotsGreenTube = formatJackpotPayload(data, games);

                                dispatch(actionsJackpots.setSkywindAndGreentubeJackpot({
                                    greenTubeJackpots: { jackpotsGreenTube },
                                    jackpotType: JACKPOTS_TYPES.GREEN_TUBE_JACKPOTS
                                }));
                            })
                            .catch((error) => {
                                console.error('Fetch Error:', error);
                            });
                    } else {
                        dispatch(actionsJackpots.setSkywindAndGreentubeJackpot({
                            greenTubeJackpots: {},
                            jackpotType: JACKPOTS_TYPES.GREEN_TUBE_JACKPOTS
                        }));
                    }
                }

                if (!skyWindJackpots?.jackpotsSkyWin) {
                    if (urlSkyWind) {
                        fetch(urlSkyWind, { headers: { 'custom-environment': envSkyWind || '' } })
                            .then((res) => res.json())
                            .then((data) => {
                                const jackpotsSkyWin = formatJackpotPayload(data, games);

                                dispatch(actionsJackpots.setSkywindAndGreentubeJackpot({
                                    skyWindJackpots: { jackpotsSkyWin },
                                    jackpotType: JACKPOTS_TYPES.SKY_WIND_JACKPOT
                                }));
                            })
                            .catch((error) => {
                                console.error('Fetch Error:', error);
                            });
                    } else {
                        dispatch(actionsJackpots.setSkywindAndGreentubeJackpot({
                            skyWindJackpots: {},
                            jackpotType: JACKPOTS_TYPES.SKY_WIND_JACKPOT
                        }));
                    }
                }

                dispatcher.dispatch('games:data', response);
                dispatch(actions.updateOldGamesFeeds(response.gamesFeeds));
            })
            .finally(() => {
                isFetching = false;
            });
    }, [
        dispatch,
        lang,
        requestStamp,
        getCMSResourceURL,
        urlGreenTube,
        envGreenTube,
        urlSkyWind,
        envSkyWind,
        isOldGamesFeedsLoaded,
        prevExcludedGameCodes,
        excludedGameCodes,
        oldGamesFeeds,
        games,
        greenTubeJackpots?.jackpotsGreenTube,
        skyWindJackpots?.jackpotsSkyWin,
        rawGames
    ]);
};
